import { useEffect, useId, useRef, useState } from 'react'
import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react'
import { cn } from '../lib/cn'

/**
 * The internal-note editor the back-office frames draw (`4807:7148`,
 * `4807:10940`, `4905:8777`).
 *
 * <p>Five controls and a word count. The frames also show a font picker, a
 * block-style picker and a zoom control; those are deliberately not built. An
 * internal note is an audit record read by colleagues and quoted into
 * decisions, and letting somebody set Comic Sans at 150% in one buys nothing
 * and makes two notes about the same case look like they came from different
 * systems. Emphasis and lists carry meaning — "what was checked, who was
 * contacted, what is still open" is a list — so those are here.
 *
 * <p>Emits HTML, which the server sanitises against a fixed allowlist on write
 * (`RichText`). Nothing here is trusted to produce safe markup: a person can
 * paste anything into a `contentEditable`, and the boundary is the only place
 * that can be sure.
 */
export type RichTextEditorProps = {
  label: string
  value: string
  onValueChange: (html: string) => void
  hint?: string
  rows?: number
  disabled?: boolean
}

type Command = {
  id: string
  label: string
  icon: React.ReactNode
  command: string
}

const COMMANDS: Command[] = [
  { id: 'bold', label: 'Gras', icon: <Bold className="size-4" />, command: 'bold' },
  { id: 'italic', label: 'Italique', icon: <Italic className="size-4" />, command: 'italic' },
  {
    id: 'underline',
    label: 'Souligné',
    icon: <Underline className="size-4" />,
    command: 'underline',
  },
  {
    id: 'ul',
    label: 'Liste à puces',
    icon: <List className="size-4" />,
    command: 'insertUnorderedList',
  },
  {
    id: 'ol',
    label: 'Liste numérotée',
    icon: <ListOrdered className="size-4" />,
    command: 'insertOrderedList',
  },
]

/** The frame's "0 mots". Counts words, not characters — that is what it says. */
function countWords(html: string): number {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return text === '' ? 0 : text.split(/\s+/).length
}

export function RichTextEditor({
  label,
  value,
  onValueChange,
  hint,
  rows = 8,
  disabled = false,
}: RichTextEditorProps) {
  const id = useId()
  const editor = useRef<HTMLDivElement>(null)
  const [words, setWords] = useState(() => countWords(value))

  // Written into the node only when it differs, and never while it has focus:
  // assigning innerHTML puts the caret back at the start, which makes typing
  // in the middle of a sentence impossible.
  useEffect(() => {
    const node = editor.current
    if (!node || document.activeElement === node) return
    if (node.innerHTML !== value) node.innerHTML = value
    setWords(countWords(value))
  }, [value])

  function apply(command: string) {
    editor.current?.focus()
    // `execCommand` is deprecated and is still the only thing every browser
    // implements for this. The alternative is a Selection/Range editor of our
    // own, which is a large amount of code to get subtly wrong.
    document.execCommand(command)
    emit()
  }

  function emit() {
    const html = editor.current?.innerHTML ?? ''
    setWords(countWords(html))
    onValueChange(html)
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <span id={`${id}-label`} className="text-xs text-slate-600">
        {label}
      </span>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div
          role="toolbar"
          aria-label={`Mise en forme — ${label}`}
          aria-controls={id}
          className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-1.5"
        >
          {COMMANDS.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={disabled}
              aria-label={c.label}
              title={c.label}
              // Kept off the tab order: a toolbar is one stop, and five buttons
              // between the label and the text is five presses to reach the
              // thing being written.
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => apply(c.command)}
              className="grid size-8 place-items-center rounded text-slate-700 hover:bg-slate-200 disabled:opacity-40"
            >
              {c.icon}
            </button>
          ))}
        </div>

        <div
          id={id}
          ref={editor}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={`${id}-label`}
          aria-describedby={hint ? `${id}-hint` : undefined}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          className={cn(
            'w-full overflow-y-auto px-3 py-2 text-sm leading-6 text-slate-900 outline-none',
            'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
            '[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
            disabled && 'bg-slate-50 text-slate-500',
          )}
          style={{ minHeight: `${rows * 1.5}rem` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        {hint ? (
          <p id={`${id}-hint`} className="text-xs text-slate-600">
            {hint}
          </p>
        ) : (
          <span />
        )}
        <p className="text-xs text-slate-600 tabular-nums" aria-live="polite">
          {words} mot{words === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  )
}
